/**
 * Relevance order for the public store search (GlobalSearch + /api/search).
 * Rank 0 = the name is exactly the query, 1 = the name starts with it, 2 = the name contains it, 3 = only the
 * description matched. Ties keep the shorter name first, then A-Z, so the result of Enter (first store) is the
 * store the shopper most likely typed. Pure and deterministic: no limit is applied here.
 */

export function searchRank(name: string, query: string): number {
    const n = (name ?? "").trim().toLowerCase();
    const q = (query ?? "").trim().toLowerCase();
    if (!q) return 3;
    if (n === q) return 0;
    if (n.startsWith(q)) return 1;
    if (n.includes(q)) return 2;
    return 3;
}

export function sortByRelevance<T extends { name: string }>(items: readonly T[], query: string): T[] {
    return items
        .map((item, index) => ({ item, index, rank: searchRank(item.name, query) }))
        .sort((a, b) =>
            a.rank - b.rank ||
            a.item.name.length - b.item.name.length ||
            a.item.name.localeCompare(b.item.name) ||
            a.index - b.index)
        .map((entry) => entry.item);
}

// --- Search response shape (client side) -------------------------------------------------------------------------

export interface SearchStoreHit { id: string; name: string; slug: string; logo: string; /** COUPON | REFERRAL: picks the link. */ pageType?: string }
export interface SearchResult { stores: SearchStoreHit[] }

export const EMPTY_SEARCH_RESULT: SearchResult = { stores: [] };

/**
 * `/api/search` answers `{ stores }`, but a 500 answers `{ error }` and a proxy may answer anything. Only the
 * documented shape is used; everything else becomes the empty result (the "No results" state) instead of a TypeError that
 * took the whole page down. Stores come back in relevance order (exact name, prefix, contains), since Enter opens the first.
 */
export function normalizeSearchResult(data: unknown, query: string): SearchResult {
    if (!data || typeof data !== "object") return EMPTY_SEARCH_RESULT;
    const { stores } = data as { stores?: unknown };
    if (!Array.isArray(stores)) return EMPTY_SEARCH_RESULT;
    return {
        stores: sortByRelevance(
            (stores as SearchStoreHit[]).filter((s) => s && typeof s.slug === "string" && typeof s.name === "string"),
            query,
        ),
    };
}
